
import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Transition } from 'react-transition-group';
import {
  Button,
  SVGIcon,
  MaterialIcon,
  HOCs,
} from '@pearsontechnology/aero-components';
import kebabCase from 'lodash/kebabCase';
import Menu from './Menu';

import DialogPortal from '../DialogPortal';
import messages from './messages';

import {
  ModalOverlay,
  ModalBackDrop,
  ModalSurface,
  ModalHeader,
  ModalHeaderIcon,
  ModalTitle,
  ModalActions,
  ModalContent,
  ModalCloseWrapper,
  ModalBodyText,
  ModalMenuWrapper,
} from './styles';

const BaseMoreButton = ({ ...rest }) => (
  <Button grey iconOnly {...rest}>
    <MaterialIcon id="more_vert" />
  </Button>
);
const { enhanceWithPopper } = HOCs;
const MoreButtonWithPopper = enhanceWithPopper(BaseMoreButton);
function Modal({ visible, children, theme, ...props }) {
  return (
    <Transition mountOnEnter unmountOnExit in={visible} timeout={400}>
      {(status) => (
        <ModalInner status={status} theme={theme} {...props}>
          {children}
        </ModalInner>
      )}
    </Transition>
  );
}

Modal.propTypes = {
  visible: PropTypes.bool,
  children: PropTypes.node,
};

function ModalInner(props) {
  const {
    children,
    onDismiss,
    title,
    icon,
    iconColor,
    status,
    maxWidth,
    menuItems,
    theme,
    closeOnOuterClick = true,
    showCloseIcon = true,
  } = props;
  const prevFocusedElementRef = useRef();
  const modalCloseRef = useRef();
  const modalSurfaceRef = useRef();

  useEffect(() => {
    const handleFocus = (event) => {
      if (!modalSurfaceRef.current.contains(event.target)) {
        modalCloseRef.current.focus();
      }
    };

    prevFocusedElementRef.current = document.activeElement;
    modalCloseRef.current.focus();
    document.addEventListener('focus', handleFocus, true);

    return function cleanup() {
      document.removeEventListener('focus', handleFocus, true);
      prevFocusedElementRef.current.focus();
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code === 'Escape') {
        onDismiss();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return function cleanup() {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onDismiss]);

  const getId = () => {
    if (title) return `modal-title_${kebabCase(title)}`;
    return null;
  };

  return (
    <DialogPortal>
      <ModalOverlay status={status}>
        <ModalBackDrop onClick={closeOnOuterClick ? onDismiss : undefined} status={status} />
        <ModalSurface
          status={status}
          role="dialog"
          // eslint-disable-next-line jsx-a11y/aria-props
          aria-modal="true"
          aria-labelledby={getId()}
          ref={modalSurfaceRef}
          theme={theme}
          onClick={(event) => event.stopPropagation()}
          maxWidth={maxWidth}
        >
          {showCloseIcon && (
            <ModalCloseWrapper theme={theme}>
              <Button
                grey
                iconOnly
                aria-label={intl.formatMessage(messages.close)}
                onClick={onDismiss}
                ref={modalCloseRef}
                theme={theme}
              >
                <MaterialIcon id="close" />
              </Button>
            </ModalCloseWrapper>
          )}
          <ModalMenuWrapper theme={theme}>
            {!!menuItems && (
              <MoreButtonWithPopper placement="bottom-end" hidepopperonclick>
                <Menu items={menuItems} />
              </MoreButtonWithPopper>
            )}
          </ModalMenuWrapper>
          {!!title && (
            <ModalHeader theme={theme}>
              {!!icon && (
                <ModalHeaderIcon theme={theme} iconColor={iconColor}>
                  <SVGIcon icon={icon} />
                </ModalHeaderIcon>
              )}
              <ModalTitle theme={theme} id={getId()}>{title}</ModalTitle>
            </ModalHeader>
          )}
          {children}
        </ModalSurface>
      </ModalOverlay>
    </DialogPortal>
  );
}

ModalInner.propTypes = {
  onDismiss: PropTypes.func,
  children: PropTypes.any,
  title: PropTypes.string,
  icon: PropTypes.string,
  iconColor: PropTypes.string,
  status: PropTypes.string,
  maxWidth: PropTypes.string,
  menuItems: PropTypes.any,
  closeOnOuterClick: PropTypes.bool,
  showCloseIcon: PropTypes.bool,
};

export default Modal;
export { ModalContent, ModalBodyText, ModalActions };
